import { Suspense, useEffect, useMemo } from 'react';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router';
import { PublicCardPage } from '@/pages/PublicCardPage';
import { SignedOutPage } from '@/pages/SignedOutPage';
import { DataErrorBoundary } from './DataErrorBoundary';
import { RequireSession } from './RequireSession';
import { PortalPage } from '@/pages/PortalPage';
import { GalleryPage } from '@/pages/GalleryPage';
import { DevConsolePage } from '@/pages/DevConsolePage';
import { apiBinding, DataProvider } from '@/client/DataProvider';
import { PublicDataProvider } from '@/client/PublicDataProvider';
import { SIGNED_OUT_PATH } from '@/client/session';
import { ShellView } from '@/screens/generated/Shell.view';
import '@/screens/generated/hover.css';
import { modelStore, setModel, useModelState } from '@/store/store';
import type { AppProps } from '@/model/ctx';
import { SCREEN_MODE } from '@/model/nav';
import { ROUTES, SCREEN_BY_PATH } from './routes';
import { installChrome } from './chrome';
import { useViewModel } from './useViewModel';
import { VoiceHud } from '@/voice/VoiceHud';
import { PositionActions } from '@/screens/composed/PositionActions';
import { AccountLink } from '@/screens/composed/AccountLink';
import { GlobalFilters } from '@/screens/composed/GlobalFilters';
import { TradeHubPopovers } from '@/screens/composed/TradeHubPopovers';
import { LockDay } from '@/screens/composed/LockDay';
import { Loaders } from '@/screens/composed/Loaders';
import { RiskValueEditor } from '@/screens/composed/RiskValueEditor';
import { CustomRuleDialog, RuleParamEditor } from '@/screens/composed/RuleEditors';
import { StrategyDialog } from '@/screens/composed/StrategyDialog';
import { DashboardEditor } from '@/screens/composed/DashboardEditor';
import { useVoice } from '@/voice/useVoice';
import { useComposerBindings } from '@/voice/useComposerBindings';

/** Mirrors the reference componentDidMount/DidUpdate: theme attribute + responsive side/positions collapse. */
function useReferenceEffects() {
  const theme = useModelState().theme;
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);
  useEffect(() => {
    const rz = () => {
      const w = window.innerWidth;
      setModel((s) => ({ sideOpen: w < 1150 ? false : s.sideOpen, posOpen: w < 900 ? false : s.posOpen }));
    };
    window.addEventListener('resize', rz);
    rz();
    return () => window.removeEventListener('resize', rz);
  }, []);
  useEffect(() => installChrome(), []);
}

/** Store.screen ↔ URL, both ways. The store is the source of truth (as the reference's state); the URL mirrors it. */
function RouteSync() {
  const navigate = useNavigate();
  const location = useLocation();
  const screen = useModelState().screen;
  useEffect(() => {
    const want = ROUTES[screen];
    if (location.pathname !== want) navigate(want + location.search, { replace: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to screen changes
  }, [screen]);
  useEffect(() => {
    const id = SCREEN_BY_PATH[location.pathname];
    if (id && id !== modelStore.getState().screen)
      setModel({ screen: id, mode: SCREEN_MODE[id] ?? modelStore.getState().mode });
    else if (!id && location.pathname === '/') navigate(ROUTES.dashboard, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to URL changes
  }, [location.pathname]);
  return null;
}

/** Test/dev hook (visreg + journeys drive the app state exactly as they drive the reference). Not present in production builds. */
if (__DEV_SURFACES__ && typeof window !== 'undefined') {
  (window as unknown as { __pf?: unknown }).__pf = {
    set: setModel,
    get: () => modelStore.getState(),
    // Which transport this *build* is talking to, as opposed to which one the harness thinks it asked for. Vite
    // bakes `import.meta.env` when the dev server starts, and the journeys reuse a running one — so a suite
    // launched with the live binding against a server started with the sim binding runs entirely on mocks while
    // every log line says otherwise. That cost an afternoon; `tests/lib/live-session.ts` now asserts this.
    binding: apiBinding,
  };
}

function Shell({ props }: { props: AppProps }) {
  const base = useViewModel(props);
  // ESC-004: at the collapsed-rail widths an EXPANDED sidebar is an overlay-weight choice, and Escape dismisses
  // it — the "dismissible" half of the ruling. Desktop keeps Escape for the surfaces that already use it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || window.innerWidth > 1024) return;
      const state = modelStore.getState();
      if (state.navOpen) setModel({ navOpen: false });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const voice = useVoice();
  // R-02: the ⌘K composer is the voice layer's text mode — same orchestrator, same skills, same guardrails.
  const m = useComposerBindings(base, voice);
  useReferenceEffects();
  // dev/test hook: drive a scripted voice turn from the visreg + journey harnesses (excluded from production builds)
  useEffect(() => {
    if (!__DEV_SURFACES__ || typeof window === 'undefined') return;
    const w = window as unknown as { __pf?: Record<string, unknown> };
    w.__pf = {
      ...(w.__pf ?? {}),
      voice: {
        ask: voice.ask,
        state: () => voice.ctx.state,
        turns: () => voice.ctx.turns,
        // Raw machine-event dispatch: the visreg suite freezes one baseline per HUD state (SM-P6-01) and forcing
        // a state through the reducer is the same path the product takes — no parallel rendering mode to drift.
        send: voice.send,
        force: voice.force,
      },
    };
  }, [voice]);
  return (
    <div className="sc-host" data-sc-name="PointFive OS Original">
      <ShellView m={m} />
      <PositionActions m={m} />
      <AccountLink m={m} />
      <GlobalFilters m={m} />
      <TradeHubPopovers m={m} />
      <LockDay m={m} />
      <Loaders m={m} />
      <RiskValueEditor m={m} />
      <RuleParamEditor m={m} />
      <CustomRuleDialog m={m} />
      <StrategyDialog m={m} />
      <DashboardEditor m={m} />
      <VoiceHud voice={voice} />
    </div>
  );
}

export function App() {
  const props = useMemo<AppProps>(() => ({ showFxTicker: true, accountCount: 5, avatarSrc: '' }), []);
  return (
    <BrowserRouter>
      <Suspense fallback={null}>
        <Routes>
          {/* Outside the boundary, and outside `DataProvider`. These three are the surfaces a person can reach
                without an account, so none of them may depend on the authenticated aggregate to render. */}
          <Route path={SIGNED_OUT_PATH} element={<SignedOutPage />} />
          <Route
            path="/card/:token"
            element={
              <PublicDataProvider>
                <PublicCardPage />
              </PublicDataProvider>
            }
          />
          <Route
            path="/portal/*"
            element={
              <PublicDataProvider>
                <PortalPage />
              </PublicDataProvider>
            }
          />
          <Route path="/gallery" element={<GalleryPage />} />
          {__DEV_SURFACES__ ? <Route path="/dev/console" element={<DevConsolePage />} /> : null}
          {/* Everything else is the trader's own product and is behind the session guard. */}
          <Route
            path="*"
            element={
              <RequireSession>
                {/* Inside the session guard and around the aggregate only: a failure of the trader's own data must
                    not take down the surfaces a person can reach without an account. */}
                <DataErrorBoundary>
                  <DataProvider>
                    <RouteSync />
                    <Shell props={props} />
                  </DataProvider>
                </DataErrorBoundary>
              </RequireSession>
            }
          />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
